import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, Button, Box, TextField, Typography, Chip, Grid, FormControl } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, FullscreenSharp, FullscreenExitSharp, Close } from '@mui/icons-material';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { isMobile, isIOS, useMobileOrientation } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getData } from '../../api/dataSlice';
import { getDetectionColor, hexToRgb, CloseIconButton, getDashboardRawData, dashboardFmtDate, databaseDateToFilename, filenameToDatabaseDate } from '../Utilities';

function getDetectionsDay(dateString, archiveSource, cameraId) {

    const { detailsData } = getDashboardRawData()

    // build detection groups by filename
    const byFile = {}
    if (detailsData) {
        const dayRows = detailsData.filter(elem => dashboardFmtDate(new Date(elem.timestamp)) === dateString)
        for (const elem of dayRows) {
            if (!byFile[elem.image_filename]) byFile[elem.image_filename] = []
            byFile[elem.image_filename].push(elem)
        }
    }

    // merge ALL images from archive source (includes those without detections)
    if (archiveSource?.[0]) {
        for (const entry of archiveSource[0]) {
            if (dashboardFmtDate(entry[0]) !== dateString) continue
            const camData = entry[1]?.[cameraId]
            if (!camData?.datum) continue
            const filename = databaseDateToFilename(camData.datum)
            if (!byFile[filename]) byFile[filename] = [] // no detections
        }
    }

    const filenames = Object.keys(byFile).sort()
    if (!filenames.length) return { date: dateString, images: [] }

    return {
        date: dateString,
        images: filenames.map(filename => {
            const group = byFile[filename]
            const detections = group.map(row => {
                const objSubtype = row.object_subtype || ''
                let coords = row.coords || []
                if (Array.isArray(coords)) {
                    coords = coords.map(elem => Array.isArray(elem) && elem.length === 1 && Array.isArray(elem[0]) ? elem[0] : elem)
                }
                return {
                    object_class: (objSubtype && objSubtype.includes('*')) ? objSubtype : (row.object_class || 'unknown'),
                    confidence: Math.round((row.confidence || 0) * 100) / 100,
                    coords,
                    bbox: row.bbox || []
                }
            })
            // reconstruct timestamp from filename for images without detections
            const timestamp = group[0]?.timestamp || (filenameToDatabaseDate(filename) + ':00') || null
            return { filename, timestamp, detections }
        })
    }
}

function formatTimestamp(timestamp) {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    const pad = (n) => String(n).padStart(2, '0')
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const navButtonSx = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 40, height: 40, borderRadius: '50%',
    bgcolor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'background 0.15s', zIndex: 2,
    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
}

export default function DetectionViewer({ date, onClose, initialEnabledTypes, initialTimestamp, initialClickedType, baseUrl, translateType, cameraId: propCameraId }) {

    const { t } = useTranslation()
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { CAMERAS } = window.conf
    const { source } = useSelector(getData)

    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [currentIdx, setCurrentIdx] = useState(0)
    const [enabledTypes, setEnabledTypes] = useState(new Set())
    const [imgAspect, setImgAspect] = useState(null)

    const fullscreenHandle = useFullScreenHandle()
    const canvasRef = useRef(null)
    const imgRef = useRef(null)
    const isMobilePortrait = isMobile && isPortrait
    const isMobileLandscape = isMobile && isLandscape
    const chipCols = isMobilePortrait ? 2 : 4
    const chipHeight = isMobile ? 22 : 24
    const avatarSize = isMobile ? 14 : 18
    const camId = propCameraId ?? CAMERAS?.[0]?.id ?? 0

    useEffect(() => {
        setLoading(true)
        try {
            const json = getDetectionsDay(date, source, camId)
            setData(json)

            let startIndex = 0
            if (initialTimestamp && json.images) {
                const target = String(initialTimestamp).substring(0, 16).replace('T', ' ')
                const index = json.images.findIndex(img => {
                    const timestamp = (img.timestamp || '').substring(0, 16).replace('T', ' ')
                    return timestamp === target
                })
                if (index >= 0) startIndex = index
            }
            if (startIndex === 0 && initialEnabledTypes?.size > 0 && json.images) {
                const typeIndex = json.images.findIndex(img => img.detections.some(detection => initialEnabledTypes.has(detection.object_class)))
                if (typeIndex >= 0) startIndex = typeIndex
            }
            setCurrentIdx(startIndex)

            // if a specific type was clicked in the chart, pre-select only that type
            if (initialClickedType) {
                setEnabledTypes(new Set([initialClickedType]))
            } else {
                const allDayTypes = new Set()
                json.images?.forEach(img => img.detections.forEach(detection => allDayTypes.add(detection.object_class)))
                if (initialEnabledTypes?.size > 0) {
                    const intersection = new Set([...initialEnabledTypes].filter(elem => allDayTypes.has(elem)))
                    setEnabledTypes(intersection.size > 0 ? intersection : allDayTypes)
                } else {
                    setEnabledTypes(allDayTypes)
                }
            }
        } catch (error) { 
            setData(null)
            console.error(`[DetectionViewer]`, error)
        }
        setLoading(false)
    }, [date]) // eslint-disable-line react-hooks/exhaustive-deps

    const drawOverlays = useCallback(() => {

        const canvas = canvasRef.current
        const img = imgRef.current
        if (!canvas || !img || !data?.images?.length) return
        if (!img.naturalWidth || !img.naturalHeight) return

        const image = data.images[currentIdx]
        if (!image) return

        const ctx = canvas.getContext('2d')
        const w = img.clientWidth, h = img.clientHeight
        canvas.width = w
        canvas.height = h
        canvas.style.width = w + 'px'
        canvas.style.height = h + 'px'
        canvas.style.top = img.offsetTop + 'px'
        canvas.style.left = img.offsetLeft + 'px'
        const scaleX = w / img.naturalWidth, scaleY = h / img.naturalHeight
        ctx.clearRect(0, 0, w, h)

        image.detections.forEach(elem => {
            if (!enabledTypes.has(elem.object_class)) return
            const color = getDetectionColor(elem.object_class)
            const rgb = hexToRgb(color)

            if (elem.coords?.length >= 3) {
                ctx.beginPath()
                ctx.moveTo(elem.coords[0][0] * scaleX, elem.coords[0][1] * scaleY)
                for (let i = 1; i < elem.coords.length; i++) ctx.lineTo(elem.coords[i][0] * scaleX, elem.coords[i][1] * scaleY)
                ctx.closePath()
                ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},0.25)`
                ctx.fill()
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.stroke()
            } else if (elem.bbox?.length === 2) {
                const x = elem.bbox[0][0] * scaleX, y = elem.bbox[0][1] * scaleY
                const bw = (elem.bbox[1][0] - elem.bbox[0][0]) * scaleX
                const bh = (elem.bbox[1][1] - elem.bbox[0][1]) * scaleY
                ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},0.2)`
                ctx.fillRect(x, y, bw, bh)
                ctx.strokeStyle = color
                ctx.lineWidth = 2
                ctx.strokeRect(x, y, bw, bh)
            }
        })
    }, [data, currentIdx, enabledTypes])

    const handleImgLoad = useCallback(() => {
        const img = imgRef.current
        if (img && img.naturalWidth && img.naturalHeight) {
            setImgAspect(img.naturalWidth / img.naturalHeight)
        }
        drawOverlays()
    }, [drawOverlays])

    // redraw on data/index/types changes (drawOverlays) and after Paper resizes from imgAspect changes
    useEffect(() => drawOverlays(), [drawOverlays, imgAspect])

    useEffect(() => {
        window.addEventListener('resize', drawOverlays)
        return () => window.removeEventListener('resize', drawOverlays)
    }, [drawOverlays])

    useEffect(() => {
        const handler = (event) => {
            if (event.key === 'Escape') onClose()
            else if (event.key === 'ArrowLeft') setCurrentIdx(prev => Math.max(0, prev - 1))
            else if (event.key === 'ArrowRight' && data?.images) setCurrentIdx(prev => Math.min(data.images.length - 1, prev + 1))
        }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [data, onClose])

    const toggleType = useCallback((type) => {
        setEnabledTypes(prev => { const next = new Set(prev); next.has(type) ? next.delete(type) : next.add(type); return next })
    }, [])

    // only types that exist on this day, sorted alphabetically
    const dayTypes = useMemo(() => {
        if (!data?.images) return []
        const set = new Set()
        data.images.forEach(img => img.detections.forEach(detection => set.add(detection.object_class)))
        return [...set].sort((a, b) => translateType(a).localeCompare(translateType(b)))
    }, [data, translateType])

    const chipRows = Math.ceil(dayTypes.length / chipCols)

    if (loading || !data?.images?.length) {
        return (
            <Dialog open onClose={onClose}>
                <CloseIconButton onClick={onClose} />
                <DialogContent sx={{ p: 2, minWidth: 250 }}>
                    <Typography sx={{ textAlign: 'center', py: 4 }}>
                        {loading ? `${t('pages.dashboard.loading_images')} ${date}...` : `${t('pages.dashboard.no_images')} ${date}.`}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={onClose}>{t('actions.close')}</Button>
                    </Box>
                </DialogContent>
            </Dialog>
        )
    }

    const currentImage = data.images[currentIdx]
    const currentCounts = {}
    
    currentImage.detections.forEach(elem => { currentCounts[elem.object_class] = (currentCounts[elem.object_class] || 0) + 1 })
    
    const allDayActive = dayTypes.length > 0 && dayTypes.every(elem => enabledTypes.has(elem))
    const hasPrev = currentIdx > 0
    const hasNext = currentIdx < data.images.length - 1

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={isMobile ? {
                sx: {
                    m: 1,
                    width: 'calc(100% - 16px)',
                    maxHeight: 'calc(100% - 16px)',
                    ...(isLandscape && {
                        bgcolor: '#0a0a1a',
                        ...(imgAspect && {
                            width: `min(calc(100vw - 16px), calc((100vh - 16px) * ${imgAspect}))`,
                            height: `min(calc(100vh - 16px), calc((100vw - 16px) / ${imgAspect}))`,
                            maxWidth: 'none',
                            maxHeight: 'none'
                        })
                    })
                }
            } : undefined}
        >

            {/* close button */}

            {isMobileLandscape ? (
                <Box onClick={onClose} sx={{ ...navButtonSx, top: 8, right: 8, transform: 'none' }}>
                    <Close sx={{ color: 'white', fontSize: 22 }} />
                </Box>
            ) : (
                <CloseIconButton onClick={onClose} />
            )}

            <DialogContent sx={{ p: isMobileLandscape ? 0 : 2 }}>
                <Grid container>
                    {!isMobileLandscape && <Grid item xs={12} sx={{ pb: 1 }} />}

                    {CAMERAS.length > 1 && !isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 2 }}>
                            <FormControl fullWidth variant="outlined">
                                <TextField label={t('events.camera')} value={CAMERAS[0]?.name || `${t('events.camera')} 1`} disabled />
                            </FormControl>
                        </Grid>
                    )}

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 2 }}>
                            <FormControl fullWidth variant="outlined">
                                <TextField label={t('date.date_n_time')} value={formatTimestamp(currentImage.timestamp)} disabled />
                            </FormControl>
                        </Grid>
                    )}

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: 0.5, flexGrow: 1, ml: 0.5 }}>
                                    {t('pages.dashboard.object_types')}
                                </Typography>
                                {!isMobilePortrait && (
                                    <Typography variant="caption" color="text.secondary">
                                        {t('pages.dashboard.image')} {currentIdx + 1} / {data.images.length} - {currentImage.detections.length} {t('pages.dashboard.total_detections')}
                                    </Typography>
                                )}
                                <Button
                                    variant="contained"
                                    size="small"
                                    disabled={allDayActive || currentImage.detections.length === 0}
                                    onClick={() => setEnabledTypes(new Set(dayTypes))}
                                    sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12 }}
                                >
                                    {t('pages.dashboard.all')}
                                </Button>
                                <Button variant="outlined" size="small" disabled={enabledTypes.size === 0 || currentImage.detections.length === 0} onClick={() => setEnabledTypes(new Set())} sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12 }}>
                                    {t('pages.dashboard.none')}
                                </Button>
                            </Box>
                        </Grid>
                    )}

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ pb: isMobile ? 1 : 2 }}>
                            <Box sx={{
                                display: 'grid',
                                gridAutoFlow: 'column',
                                gridTemplateRows: `repeat(${chipRows}, auto)`,
                                gridTemplateColumns: `repeat(${chipCols}, 1fr)`,
                                gap: 0.75
                            }}>
                                {dayTypes.map(elem => {
                                    const count = currentCounts[elem] || 0
                                    const active = enabledTypes.has(elem) && count > 0
                                    return (
                                        <Chip
                                            key={elem}
                                            size="small"
                                            avatar={<Box sx={{ borderRadius: '50%', bgcolor: getDetectionColor(elem) }} />}
                                            label={`${translateType(elem)} (${count})`}
                                            variant={active ? 'filled' : 'outlined'}
                                            onClick={() => count > 0 && toggleType(elem)}
                                            onDoubleClick={() => count > 0 && setEnabledTypes(new Set([elem]))}
                                            sx={{
                                                height: chipHeight,
                                                fontSize: isMobile ? 12 : undefined,
                                                cursor: count > 0 ? 'pointer' : 'default',
                                                justifyContent: 'flex-start',
                                                overflow: 'hidden',
                                                opacity: count > 0 ? 1 : 0.4,
                                                '& .MuiChip-avatar': { width: avatarSize, height: avatarSize, minWidth: avatarSize, ml: '4px' },
                                                '& .MuiChip-label': { pl: '8px' },
                                                ...(active && { bgcolor: '#e6ecf3', borderColor: '#003366' })
                                            }}
                                        />
                                    )
                                })}
                            </Box>
                        </Grid>
                    )}

                    <Grid item xs={12} sx={{ pb: isMobileLandscape ? 0 : 2 }}>
                        <FullScreen handle={fullscreenHandle} onChange={drawOverlays}>
                            <Box
                                onDoubleClick={() => fullscreenHandle.active ? fullscreenHandle.exit() : fullscreenHandle.enter()}
                                sx={{
                                    position: 'relative', textAlign: 'center', bgcolor: '#0a0a1a', borderRadius: 1,
                                    overflow: 'hidden', userSelect: 'none', touchAction: 'manipulation',
                                    ...(fullscreenHandle.active && { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' })
                                }}
                            >
                                <img ref={imgRef} src={`${baseUrl}${currentImage.filename}`} alt={currentImage.filename} onLoad={handleImgLoad} draggable={false}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: fullscreenHandle.active ? '100vh' : (isMobileLandscape ? '100%' : '50vh'),
                                        width: isMobileLandscape ? '100%' : undefined,
                                        height: isMobileLandscape ? '100%' : undefined,
                                        objectFit: 'contain',
                                        display: 'block',
                                        margin: isMobileLandscape ? 0 : '0 auto'
                                    }} />
                                <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }} />
                                {hasPrev && (
                                    <Box onClick={() => setCurrentIdx(prev => prev - 1)} onDoubleClick={e => e.stopPropagation()} sx={{ ...navButtonSx, left: 8 }}>
                                        <ArrowBackIos sx={{ color: 'white', fontSize: 18, ml: '4px' }} />
                                    </Box>
                                )}
                                {hasNext && (
                                    <Box onClick={() => setCurrentIdx(prev => prev + 1)} onDoubleClick={e => e.stopPropagation()} sx={{ ...navButtonSx, right: 8 }}>
                                        <ArrowForwardIos sx={{ color: 'white', fontSize: 18 }} />
                                    </Box>
                                )}
                                {!isIOS && (
                                    <Box
                                        onClick={fullscreenHandle.active ? fullscreenHandle.exit : fullscreenHandle.enter}
                                        sx={{ ...navButtonSx, bottom: 8, right: 8, top: 'auto', transform: 'none' }}
                                    >
                                        {fullscreenHandle.active
                                            ? <FullscreenExitSharp sx={{ color: 'white', fontSize: 22 }} />
                                            : <FullscreenSharp sx={{ color: 'white', fontSize: 22 }} />}
                                    </Box>
                                )}
                            </Box>
                        </FullScreen>
                    </Grid>

                    {!isMobileLandscape && (
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button variant="outlined" onClick={onClose} sx={{ width: 100 }}>{t('actions.close')}</Button>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
        </Dialog>
    )
}
